import { useEffect, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import { useApi } from "../../hooks/useApi";
import {
  listFirmProfiles,
  createFirmProfile,
  updateFirmProfile,
  deleteFirmProfile,
} from "../../api/firmProfileService";
import { showApiError, showToast } from "../../utils/toast";

// The practice's own letterhead identities. Multi-record (firm files under
// more than one legal entity). No "default" — admins pick which profile to
// use when generating an invoice. Past invoices snapshot the profile fields,
// so editing or deleting here doesn't rewrite history.
export default function FirmProfilesPage() {
  const profiles = useApi(() => listFirmProfiles(), []);
  const rows = Array.isArray(profiles.data?.data) ? profiles.data.data : [];

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null); // profile object | null
  const [busyId, setBusyId] = useState(null);

  const openCreate = () => { setEditing(null); setDrawerOpen(true); };
  const openEdit = (p) => { setEditing(p); setDrawerOpen(true); };

  const remove = async (p) => {
    if (!window.confirm(
      `Delete firm profile "${p.firm_name}"? Past invoices already carry a snapshot of these details, so this is safe — they'll still print identically.`,
    )) return;

    setBusyId(p.id);
    try {
      await deleteFirmProfile(p.id);
      showToast("success", "Firm profile deleted");
      profiles.reload();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Firm Profiles</h1>
          <div className="page-sub">
            Letterhead identities used on tax invoices. Add one per legal entity you file under.
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" size={14} /> Add profile
          </button>
        </div>
      </div>

      {profiles.loading ? (
        <div className="card"><div className="card-body"><LoadingBlock rows={4} /></div></div>
      ) : rows.length === 0 ? (
        <div className="card"><div className="card-body">
          <EmptyState
            icon="briefcase"
            title="No firm profiles yet"
            hint="Add one to use on invoices and receipts."
          />
        </div></div>
      ) : (
        <div className="col gap-md">
          {rows.map((p) => (
            <ProfileCard
              key={p.id}
              profile={p}
              busy={busyId === p.id}
              onEdit={() => openEdit(p)}
              onDelete={() => remove(p)}
            />
          ))}
        </div>
      )}

      <ProfileDrawer
        open={drawerOpen}
        profile={editing}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => { setDrawerOpen(false); profiles.reload(); }}
      />
    </>
  );
}

function ProfileCard({ profile: p, busy, onEdit, onDelete }) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="row" style={{ alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
          <div>
            <div className="t-primary" style={{ fontWeight: 600, fontSize: 16 }}>{p.firm_name}</div>
            {p.address && (
              <div className="t-muted text-xs" style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>{p.address}</div>
            )}
          </div>
          <div className="row gap-sm">
            <button className="btn btn-secondary btn-sm" onClick={onEdit}>
              <Icon name="edit" size={13} /> Edit
            </button>
            <button
              className="icon-btn"
              style={{ color: "var(--danger)" }}
              onClick={onDelete}
              disabled={busy}
              title="Delete this profile"
            >
              <Icon name="trash" size={13} />
            </button>
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "8px 24px",
          }}
        >
          <Cell label="GSTIN" value={p.gstin} mono />
          <Cell label="PAN" value={p.pan} mono />
          <Cell label="State" value={[p.state_name, p.state_code].filter(Boolean).join(" · ")} />
          <Cell label="Default HSN" value={p.default_hsn} mono />
          <Cell label="Signatory" value={p.signatory_name} />
          <Cell label="Bank" value={p.bank_name} />
          <Cell label="A/c No" value={p.bank_account_no} mono />
          <Cell label="Branch & IFSC" value={[p.bank_branch, p.bank_ifsc].filter(Boolean).join(" · ")} mono />
        </div>
      </div>
    </div>
  );
}

function Cell({ label, value, mono = false }) {
  return (
    <div>
      <div className="t-muted text-xs">{label}</div>
      <div className={mono ? "t-mono" : ""} style={{ wordBreak: "break-word" }}>
        {value || <span className="t-muted">—</span>}
      </div>
    </div>
  );
}

const EMPTY = {
  firm_name: "",
  address: "",
  gstin: "",
  pan: "",
  state_name: "",
  state_code: "",
  bank_name: "",
  bank_account_no: "",
  bank_branch: "",
  bank_ifsc: "",
  default_hsn: "",
  signatory_name: "",
  declaration_text: "",
};

const DEFAULT_DECLARATION =
  "We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.";

function ProfileDrawer({ open, profile, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const editing = !!profile;

  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (profile) {
      const out = { ...EMPTY };
      for (const k of Object.keys(EMPTY)) out[k] = profile[k] ?? "";
      setForm(out);
    } else {
      setForm(EMPTY);
    }
  }, [open, profile]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      // "" → null so the backend's regex rules don't fire on blank strings,
      // and so blanks actually clear a value rather than failing validation.
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        payload[k] = v === "" ? null : v;
      }

      if (editing) {
        await updateFirmProfile(profile.id, payload);
        showToast("success", "Firm profile updated");
      } else {
        await createFirmProfile(payload);
        showToast("success", "Firm profile created");
      }
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editing ? "Edit firm profile" : "New firm profile"}
      subtitle="Printed on the seller block of every invoice generated against this firm."
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={saving || !form.firm_name.trim()}
          >
            {saving ? "Saving…" : editing ? "Save changes" : "Create profile"}
          </button>
        </>
      }
    >
      <Section title="Identity">
        <Field label="Firm name *" error={errors.firm_name?.[0]}>
          <input
            className="input"
            value={form.firm_name}
            onChange={(e) => set({ firm_name: e.target.value })}
            placeholder="SHARMA & ASSOCIATES, CHARTERED ACCOUNTANTS"
          />
        </Field>
        <Field label="Address" error={errors.address?.[0]} hint="Multi-line allowed.">
          <textarea
            className="textarea"
            rows={3}
            value={form.address}
            onChange={(e) => set({ address: e.target.value })}
            placeholder={"#12, 3rd Cross, MG Road,\nIndiranagar,\nBengaluru, 560038"}
          />
        </Field>
        <Field label="GSTIN" error={errors.gstin?.[0]} hint="15 chars: state code + PAN + entity + Z + check.">
          <input
            className="input t-mono"
            value={form.gstin}
            onChange={(e) => set({ gstin: e.target.value.toUpperCase() })}
            placeholder="29ABCDE1234F1Z5"
            maxLength={15}
          />
        </Field>
        <Field label="PAN" error={errors.pan?.[0]} hint="10 chars: 5 letters + 4 digits + 1 letter.">
          <input
            className="input t-mono"
            value={form.pan}
            onChange={(e) => set({ pan: e.target.value.toUpperCase() })}
            placeholder="ABCDE1234F"
            maxLength={10}
          />
        </Field>
        <Field label="State name" error={errors.state_name?.[0]}>
          <input
            className="input"
            value={form.state_name}
            onChange={(e) => set({ state_name: e.target.value })}
            placeholder="Karnataka"
            maxLength={100}
          />
        </Field>
        <Field label="State code" error={errors.state_code?.[0]} hint="2-digit GST state code (first 2 chars of GSTIN).">
          <input
            className="input t-mono"
            value={form.state_code}
            onChange={(e) => set({ state_code: e.target.value })}
            placeholder="29"
            inputMode="numeric"
            maxLength={3}
          />
        </Field>
      </Section>

      <Section title="Bank">
        <Field label="Bank name" error={errors.bank_name?.[0]}>
          <input
            className="input"
            value={form.bank_name}
            onChange={(e) => set({ bank_name: e.target.value })}
            placeholder="HDFC Bank — Current A/c"
          />
        </Field>
        <Field label="A/c No" error={errors.bank_account_no?.[0]}>
          <input
            className="input t-mono"
            value={form.bank_account_no}
            onChange={(e) => set({ bank_account_no: e.target.value })}
            placeholder="50200012345678"
          />
        </Field>
        <Field label="Branch" error={errors.bank_branch?.[0]}>
          <input
            className="input"
            value={form.bank_branch}
            onChange={(e) => set({ bank_branch: e.target.value })}
            placeholder="Indiranagar"
          />
        </Field>
        <Field label="IFSC" error={errors.bank_ifsc?.[0]} hint="11 chars: 4 letters + 0 + 6 alphanumerics.">
          <input
            className="input t-mono"
            value={form.bank_ifsc}
            onChange={(e) => set({ bank_ifsc: e.target.value.toUpperCase() })}
            placeholder="HDFC0001234"
            maxLength={11}
          />
        </Field>
      </Section>

      <Section title="Invoicing defaults">
        <Field label="Default HSN/SAC" error={errors.default_hsn?.[0]} hint="Prefilled on the line items of new invoices.">
          <input
            className="input t-mono"
            value={form.default_hsn}
            onChange={(e) => set({ default_hsn: e.target.value })}
            placeholder="998224"
            maxLength={10}
          />
        </Field>
        <Field label="Authorised signatory name" error={errors.signatory_name?.[0]}>
          <input
            className="input"
            value={form.signatory_name}
            onChange={(e) => set({ signatory_name: e.target.value })}
            placeholder="e.g. CA Rohan Sharma"
          />
        </Field>
        <Field label="Declaration text" error={errors.declaration_text?.[0]} hint="Leave blank to use the standard declaration.">
          <textarea
            className="textarea"
            rows={3}
            value={form.declaration_text}
            onChange={(e) => set({ declaration_text: e.target.value })}
            placeholder={DEFAULT_DECLARATION}
          />
        </Field>
      </Section>
    </Drawer>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        className="t-muted"
        style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}
      >
        {title}
      </div>
      <div className="col gap-sm">{children}</div>
    </div>
  );
}

function Field({ label, error, hint, children }) {
  return (
    <div className="field" style={{ marginBottom: 0 }}>
      <label className="field-label">{label}</label>
      {children}
      {hint && !error && <div className="field-hint">{hint}</div>}
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}
